import { EMAIL_TOKEN_LABELS } from './email-token-labels';
import { Model } from './model';

export enum EmailType {
  warrantyProvisioned = 'Warranty Provisioned',
  warrantyRenewal = 'Warranty Renewal',
  renewalReminder = 'Renewal Reminder',
  loginLink = 'Login Link',
  claimCreated = 'Claim Created',
  claimClosed = 'Claim Closed',
  claimPaymentIssued = 'Claim Payment Issued',
  claimDocumentUploaded = 'Claim Document Uploaded',
}

export enum DeliveryMode {
  EVENT_DRIVEN = 'EVENT_DRIVEN',
  SCHEDULED = 'SCHEDULED',
}

export enum ScheduleAnchor {
  EXPIRY_DATE = 'EXPIRY_DATE',
  TERM_START_DATE = 'TERM_START_DATE',
}

export enum PolicyEmailSystemAttachment {
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  COVERAGE_SUMMARY = 'COVERAGE_SUMMARY',
  REGISTRATION_PAGE = 'REGISTRATION_PAGE',
}

export interface PolicyEmailUploadedAttachment {
  storageItemId: string;
  filename: string;
  contentType: string;
}

export const DEFAULT_WARRANTY_PROVISIONED_SYSTEM_ATTACHMENTS = [PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS];

const SCHEDULE_OFFSETS_MAX_COUNT = 12;
const SCHEDULE_OFFSETS_DAY_RANGE = 365;

export function validateScheduleOffsetsDays(offsets: number[]): string | null {
  if (offsets.length > SCHEDULE_OFFSETS_MAX_COUNT) {
    return `scheduleOffsetsDays cannot have more than ${SCHEDULE_OFFSETS_MAX_COUNT} entries`;
  }
  if (offsets.some(d => Math.abs(d) > SCHEDULE_OFFSETS_DAY_RANGE)) {
    return `scheduleOffsetsDays values must be within ±${SCHEDULE_OFFSETS_DAY_RANGE} days`;
  }
  if (new Set(offsets).size !== offsets.length) {
    return 'scheduleOffsetsDays cannot contain duplicate values';
  }
  const sorted = [...offsets].sort((a, b) => a - b);
  if (offsets.some((v, i) => v !== sorted[i])) {
    return 'scheduleOffsetsDays must be in ascending order';
  }
  return null;
}

export interface EmailToken {
  token: string;
  description: string;
  example: string;
}

export const POLICY_EMAIL_TOKENS: Partial<Record<EmailType, EmailToken[]>> = {
  [EmailType.warrantyProvisioned]: [
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{distributorName}}', ...EMAIL_TOKEN_LABELS.distributorName },
    { token: '{{warrantyName}}', ...EMAIL_TOKEN_LABELS.warrantyName },
    { token: '{{policyName}}', ...EMAIL_TOKEN_LABELS.policyName },
    { token: '{{warranty.referenceId}}', ...EMAIL_TOKEN_LABELS.warrantyReferenceId },
    { token: '{{warranty.contractNumber}}', ...EMAIL_TOKEN_LABELS.warrantyContractNumber },
    { token: '{{portalUrl}}', ...EMAIL_TOKEN_LABELS.portalUrl },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
    { token: '{{instructionImage}}', ...EMAIL_TOKEN_LABELS.instructionImage },
  ],
  [EmailType.warrantyRenewal]: [
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{distributorName}}', ...EMAIL_TOKEN_LABELS.distributorName },
    { token: '{{warrantyName}}', ...EMAIL_TOKEN_LABELS.warrantyName },
    { token: '{{policyName}}', ...EMAIL_TOKEN_LABELS.policyName },
    { token: '{{warranty.referenceId}}', ...EMAIL_TOKEN_LABELS.warrantyReferenceId },
    { token: '{{warranty.contractNumber}}', ...EMAIL_TOKEN_LABELS.warrantyContractNumber },
    { token: '{{warranty.expiryDate}}', ...EMAIL_TOKEN_LABELS.warrantyExpiryDate },
    { token: '{{portalUrl}}', ...EMAIL_TOKEN_LABELS.portalUrl },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
  [EmailType.loginLink]: [
    { token: '{{loginUrl}}', ...EMAIL_TOKEN_LABELS.loginUrl },
    { token: '{{userName}}', ...EMAIL_TOKEN_LABELS.userName },
    { token: '{{brandName}}', ...EMAIL_TOKEN_LABELS.brandName },
    { token: '{{productName}}', ...EMAIL_TOKEN_LABELS.productName },
    { token: '{{redirectButton}}', ...EMAIL_TOKEN_LABELS.redirectButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
  [EmailType.claimCreated]: [
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{claimId}}', ...EMAIL_TOKEN_LABELS.claimId },
    { token: '{{claimUrl}}', ...EMAIL_TOKEN_LABELS.claimUrl },
    { token: '{{claimButton}}', ...EMAIL_TOKEN_LABELS.claimButton },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
  [EmailType.claimClosed]: [
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{claimId}}', ...EMAIL_TOKEN_LABELS.claimId },
    { token: '{{claimUrl}}', ...EMAIL_TOKEN_LABELS.claimUrl },
    { token: '{{claimButton}}', ...EMAIL_TOKEN_LABELS.claimButton },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
  [EmailType.claimPaymentIssued]: [
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{claimId}}', ...EMAIL_TOKEN_LABELS.claimId },
    { token: '{{claimUrl}}', ...EMAIL_TOKEN_LABELS.claimUrl },
    { token: '{{claimButton}}', ...EMAIL_TOKEN_LABELS.claimButton },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
  [EmailType.claimDocumentUploaded]: [
    { token: '{{customerFirstName}}', ...EMAIL_TOKEN_LABELS.customerFirstName },
    { token: '{{customerName}}', ...EMAIL_TOKEN_LABELS.customerName },
    { token: '{{claimNumber}}', ...EMAIL_TOKEN_LABELS.claimNumber },
    { token: '{{claimId}}', ...EMAIL_TOKEN_LABELS.claimId },
    { token: '{{claimUrl}}', ...EMAIL_TOKEN_LABELS.claimUrl },
    { token: '{{claimButton}}', ...EMAIL_TOKEN_LABELS.claimButton },
    { token: '{{portalButton}}', ...EMAIL_TOKEN_LABELS.portalButton },
    { token: '{{productTeam}}', ...EMAIL_TOKEN_LABELS.productTeam },
    { token: '{{currentYear}}', ...EMAIL_TOKEN_LABELS.currentYear },
    { token: '{{supportEmail}}', ...EMAIL_TOKEN_LABELS.supportEmail },
  ],
};

export interface Email {
  readonly id: string;
  enabled: boolean;
  hideDefaultContent: boolean;

  readonly created: Date;
  readonly modified: Date;

  policyId: string;
  policy?: Model;

  type: EmailType;
  templateLabel?: string | null;

  deliveryMode: DeliveryMode;
  scheduleAnchor: ScheduleAnchor | null;
  scheduleOffsetsDays: number[];
  additionalRecipients: string[];
  systemAttachments: PolicyEmailSystemAttachment[];
  uploadedAttachments: PolicyEmailUploadedAttachment[];

  subject: string | null;
  content: string | null;
  byline: string | null;

  instructionImage: string | null;
}
